import { SchoolGroup, SchoolGroupSource, SchoolGroupStatus } from "@core/models/domain/group.domain";
import { SchoolGroupInfoResponse } from "@core/models/response/group.response";
import { buildSchoolUserFromResponse } from "./account.converter";

/**
 * Converts a `SchoolGroupInfoResponse` object into a `SchoolGroup` domain object.
 *
 * This function maps the properties of the response object to the corresponding fields in the domain object.
 * It also converts the `members` field by mapping each user response to a `SchoolUser` object using the `buildSchoolUserFromResponse` converter.
 * Additionally, it parses the `creationDatetime` and `lastModifiedDatetime` fields into JavaScript `Date` objects.
 *
 * @param {SchoolGroupInfoResponse} response - The response object containing group information.
 * @returns {SchoolGroup} A domain object representing the school group.
 */
export function buildSchoolGroupFromResponse(response: SchoolGroupInfoResponse): SchoolGroup {
    return {
        id: response.id,
        schoolAccountId: response.schoolAccountId,
        identifierCode: response.identifierCode,
        wondeId: response.wondeId,
        misId: response.misId,
        type: response.type,
        code: response.code,
        subject: response.subject,
        alternative: response.alternative,
        priority: response.priority,
        academicYear: response.academicYear,
        yearGroup: response.yearGroup,
        name: response.name,
        description: response.description,
        status: response.status as SchoolGroupStatus,
        source: (response.source || undefined) as SchoolGroupSource | undefined,
        adoptedFromCustom: response.adoptedFromCustom,
        members: response.members?.map(userResponse => buildSchoolUserFromResponse({...userResponse, schoolGroups: undefined})) || [],
        creationDatetime: new Date(response.creationDatetime),
        lastModifiedDatetime: new Date(response.lastModifiedDatetime),
    };
}